import React, { useMemo, useState } from "react";

import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SearchIcon from "@material-ui/icons/Search";

import api from "../../services/api";
import TemplatePreview from "../TemplatePreview";
import {
  buildBodyToSave,
  buildTemplateSnapshot,
  extractVariablesByComponent,
  parseComponents,
  templateText,
} from "../../helpers/templateVariables";

const ALL_QUEUES = "all";
const NO_QUEUE = "none";

const COMPONENT_LABELS = {
  header: "Cabeçalho",
  body: "Mensagem",
  footer: "Rodapé",
  buttons: "Botões",
};

// Espelha TemplateVariableSource no backend (helpers/TemplateVariables.ts)
const SOURCE_LABELS = {
  agent_text: "Texto do atendente",
  contact_name: "Nome do contato",
  contact_number: "Número do contato",
  attendant_name: "Nome do atendente",
  today: "Data de hoje",
  ticket_id: "Número do ticket",
  fixed: "Texto fixo",
};

const useStyles = makeStyles((theme) => ({
  title: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  content: {
    minHeight: 320,
  },
  filters: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  queueFilter: {
    minWidth: 160,
  },
  templateItem: {
    display: "block",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  templateName: {
    fontWeight: 600,
  },
  templateText: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: theme.palette.text.secondary,
  },
  categoryChip: {
    height: 18,
    fontSize: "0.7rem",
    marginTop: theme.spacing(0.5),
  },
  previewLabel: {
    display: "block",
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  sectionLabel: {
    display: "block",
    marginTop: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  fieldsHint: {
    display: "block",
    marginTop: theme.spacing(2),
  },
  empty: {
    padding: theme.spacing(4, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

/**
 * Converte o retorno de /messages-template/:ticketId/:templateId/variables no
 * formato que o envio espera — variables[tipo][indice] = { value, buttonIndex }.
 */
const mapResolvedVariables = (resolved) => {
  const values = {};
  const sources = {};

  ["header", "body"].forEach((type) => {
    (resolved?.[type] || []).forEach((variable, index) => {
      if (variable?.source) sources[`${type}-${index}`] = variable.source;
      if (!variable?.value) return;
      values[type] = {
        ...values[type],
        [index]: { value: variable.value, buttonIndex: 0 },
      };
    });
  });

  (resolved?.buttons || []).forEach((variable, position) => {
    if (variable?.source) sources[`buttons-${position}`] = variable.source;
    if (!variable?.value) return;
    values.buttons = {
      ...values.buttons,
      [position]: {
        value: variable.value,
        buttonIndex: variable.index ?? position,
      },
    };
  });

  return { values, sources };
};

const TemplateModal = ({ open, handleClose, templates, onSelectTemplate, ticketId }) => {
  const classes = useStyles();

  const [search, setSearch] = useState("");
  const [queueFilter, setQueueFilter] = useState(ALL_QUEUES);
  const [selected, setSelected] = useState(null);
  const [values, setValues] = useState({});
  const [sources, setSources] = useState({});
  const [loadingVariables, setLoadingVariables] = useState(false);

  const components = useMemo(() => parseComponents(selected), [selected]);
  const variables = useMemo(
    () => extractVariablesByComponent(components),
    [components]
  );
  // Na tela, variável ainda vazia continua aparecendo como {{2}}
  const snapshot = useMemo(
    () => buildTemplateSnapshot(components, values, { keepEmpty: true }),
    [components, values]
  );

  const fields = useMemo(
    () =>
      Object.keys(variables).flatMap((componentType) =>
        variables[componentType].map((variable, index) => ({
          ...variable,
          componentType,
          index,
        }))
      ),
    [variables]
  );

  const pendingFields = fields.some(
    (field) => !values[field.componentType]?.[field.index]?.value?.trim()
  );

  // Só as filas que realmente têm template: o atendente pode estar em várias.
  const queueOptions = useMemo(() => {
    const byId = new Map();
    (templates || []).forEach((template) => {
      (template?.queues || []).forEach((queue) => byId.set(queue.id, queue.name));
    });

    return Array.from(byId, ([id, name]) => ({ id, name }));
  }, [templates]);

  const hasTemplateWithoutQueue = useMemo(
    () => (templates || []).some((template) => !template?.queues?.length),
    [templates]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    return (templates || []).filter((template) => {
      const queueIds = (template?.queues || []).map((queue) => queue.id);

      if (queueFilter === NO_QUEUE && queueIds.length > 0) return false;
      if (
        queueFilter !== ALL_QUEUES &&
        queueFilter !== NO_QUEUE &&
        !queueIds.includes(queueFilter)
      ) {
        return false;
      }

      if (!term) return true;

      const name = template?.shortcode?.toLowerCase() || "";
      const text = templateText(parseComponents(template)).toLowerCase();
      return name.includes(term) || text.includes(term);
    });
  }, [templates, search, queueFilter]);

  const backToList = () => {
    setSelected(null);
    setValues({});
    setSources({});
  };

  const closeModal = () => {
    backToList();
    setSearch("");
    setQueueFilter(ALL_QUEUES);
    handleClose();
  };

  const handleSelect = async (template) => {
    setSelected(template);
    setValues({});
    setSources({});

    if (!ticketId) return;

    setLoadingVariables(true);
    try {
      const { data } = await api.get(
        `/messages-template/${ticketId}/${template.id}/variables`
      );
      const resolved = mapResolvedVariables(data);
      setValues(resolved.values);
      setSources(resolved.sources);
    } catch (err) {
      // Sem preenchimento automático o atendente ainda consegue digitar tudo,
      // então a falha não pode bloquear o envio.
    } finally {
      setLoadingVariables(false);
    }
  };

  const handleChange = (componentType, index, buttonIndex, value) => {
    setValues((current) => ({
      ...current,
      [componentType]: {
        ...current[componentType],
        [index]: { value, buttonIndex },
      },
    }));
  };

  const handleSend = () => {
    onSelectTemplate({
      ...selected,
      variables: values,
      bodyToSave: buildBodyToSave(components, values),
      // Vai gravado na mensagem para o chat exibir igual a esta tela
      templateSnapshot: buildTemplateSnapshot(components, values),
    });
    backToList();
  };

  const fieldHelperText = (componentType, index, variable) => {
    const source = sources[`${componentType}-${index}`];

    if (source && source !== "agent_text") {
      return `Preenchido automaticamente · ${SOURCE_LABELS[source] || source}`;
    }

    return variable.example ? `Ex.: ${variable.example}` : "";
  };

  const renderList = () => (
    <>
      <div className={classes.filters}>
        <TextField
          placeholder="Pesquisar template"
          variant="outlined"
          size="small"
          fullWidth
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {queueOptions.length > 0 && (
          <FormControl variant="outlined" size="small" className={classes.queueFilter}>
            <InputLabel id="template-queue-filter">Fila</InputLabel>
            <Select
              labelId="template-queue-filter"
              label="Fila"
              value={queueFilter}
              onChange={(e) => setQueueFilter(e.target.value)}
            >
              <MenuItem value={ALL_QUEUES}>Todas</MenuItem>
              {queueOptions.map((queue) => (
                <MenuItem key={queue.id} value={queue.id}>
                  {queue.name}
                </MenuItem>
              ))}
              {hasTemplateWithoutQueue && (
                <MenuItem value={NO_QUEUE}>Sem fila</MenuItem>
              )}
            </Select>
          </FormControl>
        )}
      </div>

      {filtered.length === 0 ? (
        <Typography className={classes.empty}>
          {templates?.length
            ? "Nenhum template encontrado para esta busca."
            : "Nenhum template aprovado nesta empresa. Sincronize os templates na tela de Conexões."}
        </Typography>
      ) : (
        <List disablePadding>
          {filtered.map((template) => (
            <ListItem
              key={template.id}
              button
              className={classes.templateItem}
              onClick={() => handleSelect(template)}
            >
              <ListItemText
                disableTypography
                primary={
                  <Typography className={classes.templateName}>
                    {template.shortcode}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography variant="body2" className={classes.templateText}>
                      {templateText(parseComponents(template))}
                    </Typography>
                    {(template.queues || []).map((queue) => (
                      <Chip
                        key={queue.id}
                        size="small"
                        label={queue.name}
                        className={classes.categoryChip}
                        style={{
                          backgroundColor: queue.color,
                          color: "#fff",
                          marginRight: 4,
                        }}
                      />
                    ))}
                    {template.category && (
                      <Chip
                        size="small"
                        label={template.category}
                        className={classes.categoryChip}
                      />
                    )}
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </>
  );

  const renderForm = () => (
    <>
      <Typography variant="caption" className={classes.previewLabel}>
        Como o cliente vai receber
      </Typography>

      <TemplatePreview snapshot={snapshot} />

      {loadingVariables && (
        <Typography variant="caption" className={classes.fieldsHint}>
          <CircularProgress size={12} style={{ marginRight: 6 }} />
          Buscando o preenchimento automático...
        </Typography>
      )}

      {fields.length === 0 ? (
        <Typography variant="body2" color="textSecondary" className={classes.fieldsHint}>
          Este template não tem variáveis: é só enviar.
        </Typography>
      ) : (
        Object.keys(variables).map((componentType) =>
          variables[componentType].length === 0 ? null : (
            <div key={componentType}>
              <Typography variant="caption" className={classes.sectionLabel}>
                {COMPONENT_LABELS[componentType]}
              </Typography>
              {variables[componentType].map((variable, index) => (
                <TextField
                  key={`${componentType}-${index}`}
                  label={variable.label}
                  placeholder={variable.example}
                  helperText={fieldHelperText(componentType, index, variable)}
                  value={values[componentType]?.[index]?.value || ""}
                  onChange={(e) =>
                    handleChange(
                      componentType,
                      index,
                      variable.index || 0,
                      e.target.value
                    )
                  }
                  variant="outlined"
                  size="small"
                  margin="dense"
                  fullWidth
                />
              ))}
            </div>
          )
        )
      )}
    </>
  );

  return (
    <Dialog open={open} onClose={closeModal} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle disableTypography className={classes.title}>
        {selected && (
          <IconButton size="small" onClick={backToList}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <Typography variant="h6">
          {selected ? selected.shortcode : "Templates do WhatsApp"}
        </Typography>
      </DialogTitle>

      <DialogContent dividers className={classes.content}>
        {selected ? renderForm() : renderList()}
      </DialogContent>

      <DialogActions>
        <Button onClick={closeModal} color="secondary">
          Cancelar
        </Button>
        {selected && (
          <Button
            onClick={handleSend}
            color="primary"
            variant="contained"
            disabled={pendingFields || loadingVariables}
          >
            Enviar template
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TemplateModal;
